import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import {
  desktopBodyScrollCss,
  desktopContentColumnCss,
  desktopFooterHostCss,
  desktopPageHeaderCss,
  mobileDetailCss,
  mobileDetailScrollCss,
  mobileFooterHostCss,
} from './styles'

const SettingsModalLayoutContext = createContext(null)

/**
 * Optional layout shell for settings content shown inside SettingsModal.
 * - Desktop: wrapper uses `display: contents` so page header + scroll + footer are grid areas on `desktopShellCss`.
 * - Renders a scrollable main region and a footer host below it (Figma-style fixed footer).
 * - Embed pages keep their <Form> intact and portal Save/Cancel into `footerHost` so submit/reset keep working.
 * Reuse for profile, branding, etc.: read `useSettingsModalLayoutOptional()` and portal form actions when
 * `showFooterHost` is true. For chrome-only differences, use `useIsSettingsModalLayout()`. See `settings_modal.js`.
 */
export const SettingsModalContentColumn = ({
  children,
  header = null,
  showFooterHost = false,
  variant = 'desktop',
  dataTestId,
}) => {
  const [footerHost, setFooterHost] = useState(null)
  const [footerSuppressed, setFooterSuppressed] = useState(false)

  const footerRef = useCallback((node) => {
    setFooterHost(node)
  }, [])

  const effectiveShowFooterHost = showFooterHost && !footerSuppressed

  const value = useMemo(
    () => ({ footerHost, showFooterHost: effectiveShowFooterHost, setFooterSuppressed }),
    [footerHost, effectiveShowFooterHost],
  )

  const columnClass = variant === 'mobile' ? mobileDetailCss : desktopContentColumnCss
  const scrollClass = variant === 'mobile' ? mobileDetailScrollCss : desktopBodyScrollCss
  const footerClass = variant === 'mobile' ? mobileFooterHostCss : desktopFooterHostCss

  return (
    <SettingsModalLayoutContext.Provider value={value}>
      <div className={columnClass} data-testid={dataTestId}>
        {variant === 'desktop' && header != null ? (
          <div className={desktopPageHeaderCss} data-testid="settings-modal-page-header">
            {header}
          </div>
        ) : null}
        <div className={scrollClass}>{children}</div>
        {effectiveShowFooterHost ? (
          <div
            ref={footerRef}
            className={footerClass}
            data-testid="settings-modal-footer-host"
          />
        ) : null}
      </div>
    </SettingsModalLayoutContext.Provider>
  )
}

export const useSettingsModalLayoutOptional = () => useContext(SettingsModalLayoutContext)

/** True when content is under {@link SettingsModalContentColumn} (embedded settings layout). */
export const useIsSettingsModalLayout = () => useContext(SettingsModalLayoutContext) != null
